"use client";

/**
 * The two badge clusters both product cards render: the chips overlaid on the
 * image (Sold out / Add-on) and the taxonomy row under it (category +
 * availability).
 *
 * ── WHY THE CLASS STRINGS ARE A TABLE OF FULL LITERALS ───────────────────────
 * The desktop grid card and the mobile list row differ here ONLY in scale —
 * `px-2`/`text-[11px]`/`gap-1.5` against `px-1.5`/`text-[10px]`/`gap-1`. The
 * tempting shape is to compose a class string from a few size tokens, and that
 * is exactly the shape to avoid: composition reorders classes, and this repo has
 * no formatter and no visual-regression tooling, so a reordered class string is
 * an unreviewable diff. Each layout therefore names its classes in FULL, in the
 * original order, and the component only picks a row from the table. That keeps
 * `tests/unit/harness/product-card-dom-baseline.spec.tsx` able to prove the
 * rendered markup is byte-identical to what shipped before the extraction.
 *
 * `layout` is deliberately NOT `"mobile" | "desktop"`: both cards can be on a
 * phone (the desktop card is what a carousel rail renders at every width), so
 * the axis is the card's SHAPE — a grid `card` or a list `row`.
 */

import type { Product } from "@/stores/product-store";
import {
  AVAILABILITY_TONE_CLASS,
  availabilityBadge,
  categoryDisplayLabel,
} from "../product-card-utils";

export type ProductCardLayout = "card" | "row";

const OVERLAY_CLASS: Record<
  ProductCardLayout,
  { container: string; soldOut: string; addon: string }
> = {
  card: {
    container: "absolute top-2 left-2 flex flex-col gap-1",
    soldOut:
      "rounded-md bg-foreground/80 px-2 py-0.5 text-[11px] font-semibold text-background backdrop-blur-sm",
    addon:
      "rounded-md bg-primary/90 px-2 py-0.5 text-[11px] font-semibold text-primary-foreground backdrop-blur-sm",
  },
  row: {
    container: "absolute top-1.5 left-1.5 flex flex-col gap-1",
    soldOut:
      "rounded-md bg-foreground/80 px-1.5 py-0.5 text-[10px] font-semibold text-background backdrop-blur-sm",
    addon:
      "rounded-md bg-primary/90 px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground backdrop-blur-sm",
  },
};

const TAG_CLASS: Record<
  ProductCardLayout,
  { container: string; category: string; availability: string }
> = {
  card: {
    container: "flex flex-wrap gap-1.5",
    category:
      "rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground",
    // The tone class is appended, so this half must NOT end in a space.
    availability: "rounded-full px-2 py-0.5 text-[11px] font-medium",
  },
  row: {
    container: "flex flex-wrap gap-1",
    category:
      "rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground",
    availability: "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
  },
};

/** Chips overlaid on the card image, top-left, stacked. */
export function ProductCardOverlayBadges({
  product,
  isOutOfStock,
  layout,
}: {
  product: Product;
  isOutOfStock: boolean;
  layout: ProductCardLayout;
}) {
  const c = OVERLAY_CLASS[layout];
  return (
    <div className={c.container}>
      {isOutOfStock && <span className={c.soldOut}>Sold out</span>}
      {product.isAddon ? (
        <span
          className={c.addon}
          title="Add-on — pairs with a bouquet or arrangement"
        >
          Add-on
        </span>
      ) : null}
    </div>
  );
}

/**
 * Category chip plus the category-aware availability chip. `availabilityBadge`
 * is called WITHOUT `inStock` on purpose: the cards show sold-out state via the
 * image overlay and the footer, so passing it here would print "Sold out" three
 * times on one card. That two-argument call is the pre-existing behaviour of
 * both cards and is load-bearing for the recorded markup baseline.
 */
export function ProductCardTagRow({
  product,
  layout,
}: {
  product: Product;
  layout: ProductCardLayout;
}) {
  const c = TAG_CLASS[layout];
  const a = availabilityBadge(product.category, product.deliveryTiming);
  return (
    <div className={c.container}>
      <span className={c.category}>{categoryDisplayLabel(product.category)}</span>
      {a ? (
        <span className={`${c.availability} ${AVAILABILITY_TONE_CLASS[a.tone]}`}>
          {a.label}
        </span>
      ) : null}
    </div>
  );
}
